import { z } from "zod";

// TRBUD-427. Fixed wire contract; no delivery permissions or live routes implied.
const key = z.string().regex(/^[A-Za-z0-9_-]{1,80}$/);
const exactText = (max: number) => z.string().min(1).max(max)
  .refine((value) => value === value.trim(), "Surrounding whitespace is not allowed.");
const timestamp = z.iso.datetime({ precision: 3 });
const unknown = z.object({ state: z.literal("unknown") }).strict();
const contact = z.discriminatedUnion("state", [unknown, z.object({
  state: z.literal("known"), value: exactText(254).nullable(), observedAt: timestamp,
}).strict()]);
const equipment = z.object({
  // This is a Sylectus reference, never an SB or TB database primary key.
  source: z.literal("sylectus"), kind: z.enum(["vehicle", "trailer"]),
  id: exactText(120), vin: exactText(17).nullable(),
}).strict();
const assignmentSource = z.enum(["driver_profile", "vehicle_book", "reviewed"]);
const assignment = z.discriminatedUnion("state", [unknown,
  z.object({ state: z.literal("known"), value: equipment.nullable(),
    source: assignmentSource, observedAt: timestamp }).strict(),
  z.object({ state: z.literal("conflict"), candidates: z.array(z.object({
    equipment, source: assignmentSource, observedAt: timestamp,
  }).strict()).min(2).max(10) }).strict(),
]);
const team = z.discriminatedUnion("state", [unknown, z.object({
  state: z.literal("known"), observedAt: timestamp,
  value: z.object({ id: key, members: z.array(key).min(2).max(10)
    .refine((items) => new Set(items).size === items.length, "Duplicate team members.") }).strict().nullable(),
}).strict()]);

export const sbDriverContactSchema = z.object({
  dabcode: key, driverId: exactText(80).nullable(), name: exactText(160),
  lifecycle: z.enum(["active", "retired", "unknown"]),
  contacts: z.object({ cellPhone: contact, phone: contact, email: contact }).strict(),
  assignment, team,
}).strict();
export type SbDriverContact = z.infer<typeof sbDriverContactSchema>;

export const sbDriverSnapshotMetadataSchema = z.object({
  protocolVersion: z.literal(1), feed: z.literal("sylectus.driver-contacts"),
  environment: z.enum(["local", "staging", "production"]), snapshotId: z.string().uuid(),
  // Decimal string is ordered numerically and never converted to a JS number.
  sequence: z.string().regex(/^[1-9][0-9]{0,19}$/),
  businessDigest: z.string().regex(/^[a-f0-9]{64}$/),
  payloadDigest: z.string().regex(/^[a-f0-9]{64}$/),
  sourceRunId: key, trigger: z.enum(["scheduled", "manual", "source_edit"]),
  observedAt: timestamp, publishedAt: timestamp,
  scope: z.object({ id: key, kind: z.literal("driver_roster"), complete: z.literal(true) }).strict(),
  recordCount: z.number().int().min(0).max(10000),
}).strict().refine((value) => value.observedAt <= value.publishedAt,
  "Source observation must not follow publication.");
export type SbDriverSnapshotMetadata = z.infer<typeof sbDriverSnapshotMetadataSchema>;

export const sbDriverSnapshotSchema = z.object({
  metadata: sbDriverSnapshotMetadataSchema,
  drivers: z.array(sbDriverContactSchema).max(10000),
}).strict().superRefine(({ metadata, drivers }, ctx) => {
  const problem = (message: string) => ctx.addIssue({ code: "custom", message });
  if (metadata.recordCount !== drivers.length) problem("Record count does not match.");
  const byCode = new Map(drivers.map((driver) => [driver.dabcode, driver]));
  const teams = new Map<string, string>();
  if (byCode.size !== drivers.length) problem("Duplicate permanent driver codes.");
  for (const [index, driver] of drivers.entries()) {
    if (index && drivers[index - 1]!.dabcode >= driver.dabcode) problem("Drivers must be ordered by permanent code.");
    const fields = [driver.contacts.cellPhone, driver.contacts.phone, driver.contacts.email, driver.team];
    for (const field of fields) {
      if (field.state === "known" && field.observedAt > metadata.publishedAt) problem("Field observation follows publication.");
    }
    if (driver.assignment.state === "known" && driver.assignment.observedAt > metadata.publishedAt) problem("Assignment observation follows publication.");
    if (driver.assignment.state === "conflict" && driver.assignment.candidates.some((item) => item.observedAt > metadata.publishedAt)) problem("Assignment observation follows publication.");
    if (driver.lifecycle === "retired" && driver.assignment.state === "known" && driver.assignment.value !== null) problem("Retired drivers cannot have a confirmed current assignment.");
    if (driver.team.state !== "known" || driver.team.value === null) continue;
    const { id, members } = driver.team.value;
    const memberKey = JSON.stringify(members);
    if (teams.has(id) && teams.get(id) !== memberKey) problem("A team ID must identify one member set.");
    teams.set(id, memberKey);
    if (driver.lifecycle === "retired") problem("Retired drivers cannot have a confirmed current team.");
    if (!members.includes(driver.dabcode)) problem("Team must include its driver.");
    if (members.some((code, i) => i > 0 && members[i - 1]! >= code)) problem("Team members must be ordered by permanent code.");
    for (const code of members) {
      const peer = byCode.get(code);
      if (!peer || peer.lifecycle === "retired" || peer.team.state !== "known" || peer.team.value?.id !== id ||
        JSON.stringify(peer.team.value.members) !== JSON.stringify(members)) problem("Team membership must be reciprocal within the roster.");
    }
  }
});
export type SbDriverSnapshot = z.infer<typeof sbDriverSnapshotSchema>;

// Metadata only: the durable callback queue must never contain driver contacts.
export const sbDriverSnapshotNotificationSchema = z.object({
  protocolVersion: z.literal(1), messageId: z.string().uuid(), snapshot: sbDriverSnapshotMetadataSchema,
}).strict();
export type SbDriverSnapshotNotification = z.infer<typeof sbDriverSnapshotNotificationSchema>;

export const SB_DRIVER_SNAPSHOT_MAX_BYTES = 2 * 1024 * 1024;
export const SB_DRIVER_NOTIFICATION_MAX_BYTES = 4096;
export const SB_DRIVER_STALE_AFTER_MS = 48 * 60 * 60 * 1000;
export const SB_DRIVER_PEER_REFUSALS = ["refresh_in_progress", "cooldown", "source_cooldown", "feed_paused", "refresh_disabled",
  "publishing_off", "environment_mismatch", "scope_mismatch", "invalid_request", "request_conflict", "not_configured", "auth_failed"] as const;
export type SbDriverPeerRefusal = typeof SB_DRIVER_PEER_REFUSALS[number];
export const sbDriverProblemMessages: Record<string, string> = {
  transport: "Could not confirm SB's response. The same request will be retried.",
  invalid_response: "SB returned an incompatible response. Check the feed contract before retrying.",
  refresh_in_progress: "SB already has a fresh read in progress. This request is waiting its turn.",
  cooldown: "SB's refresh cooldown is active. Its default is 30 minutes; this request will wait for the next permitted attempt.",
  source_cooldown: "SB is backing off Sylectus logins. Wait for the indicated retry time.",
  feed_paused: "TruckBuddy access is off in SB. Turn it on in SB's TruckBuddy driver feed controls before retrying.",
  refresh_disabled: "TruckBuddy refresh requests are off in SB. Enable that switch in SB before retrying.",
  publishing_off: "Snapshot publishing is off in SB. Enable publishing before requesting a fresh read.",
  environment_mismatch: "SB rejected the configured environment. Both applications must use the same environment.",
  scope_mismatch: "SB rejected the configured roster. Use sb-shared-driver-roster for this SB producer.",
  invalid_request: "SB rejected the request format. Check the shared API contract.",
  request_conflict: "SB rejected a reused request identity with conflicting content. Inspect the request before retrying.",
  not_configured: "SB's driver feed environment is not configured yet.",
  auth_failed: "SB rejected TB's read credential or environment header. Check the dedicated key and matching environment.",
  source_failed: "SB's source read failed or was incomplete. Last good values are retained.",
  coverage_rejected: "SB rejected the read's coverage. Last good values are retained.",
  cancelled: "SB cancelled the fresh read. Check its publishing controls and worker status.",
};

export const sbDriverRefreshRequestSchema = z.object({ protocolVersion: z.literal(1), requestId: z.string().uuid(),
  environment: z.enum(["local", "staging", "production"]), scopeId: key }).strict();
export type SbDriverRefreshRequest = z.infer<typeof sbDriverRefreshRequestSchema>;
export const sbDriverRefreshStatusSchema = sbDriverRefreshRequestSchema.extend({
  state: z.enum(["queued", "running", "published", "failed"]), acceptedAt: timestamp,
  snapshot: sbDriverSnapshotMetadataSchema.nullable(), failureCode: z.enum(["source_failed", "coverage_rejected", "cancelled"]).nullable(),
}).strict().superRefine((value, ctx) => {
  const invalid = () => ctx.addIssue({ code: "custom", message: "Invalid fresh refresh result." });
  if (value.state === "published") {
    if (!value.snapshot || value.failureCode !== null || value.snapshot.environment !== value.environment ||
      value.snapshot.scope.id !== value.scopeId || value.snapshot.trigger !== "manual" || value.snapshot.observedAt < value.acceptedAt) invalid();
  } else if (value.state === "failed") {
    if (value.snapshot !== null || value.failureCode === null) invalid();
  } else if (value.snapshot !== null || value.failureCode !== null) invalid();
});
export type SbDriverRefreshStatus = z.infer<typeof sbDriverRefreshStatusSchema>;

export const sbDriverFeedControlsSchema = z.object({ expectedVersion: z.number().int().min(0),
  receiveEnabled: z.boolean(), importEnabled: z.boolean(), retryLatest: z.boolean().default(false) }).strict();
export type SbDriverFeedControls = z.infer<typeof sbDriverFeedControlsSchema>;
export interface SbDriverFeedStatus {
  configured: boolean; enabled: boolean; environment: string | null; scopeId: string | null; origin: string | null;
  receiveEnabled: boolean; importEnabled: boolean; controlVersion: number;
  checkpoint: SbDriverSnapshotMetadata | null; importedAt: string | null; lastRunAt: string | null; failureCode: string | null;
  currentDrivers: number; unmatchedDrivers: number;
  mappingIssues: { dabcode: string; state: string; equipmentIds: string[] }[];
  refresh: { requestId: string; state: string; requestedAt: string; acceptedAt: string | null;
    snapshot: SbDriverSnapshotMetadata | null; failureCode: string | null; attempts: number; retryAt?: string | null } | null;
  publications: { snapshotId: string; sequence: string; state: string; attempts: number; receivedAt: string; nextAttemptAt: string }[];
}
export interface SbDriverFeedPreview {
  metadata: SbDriverSnapshotMetadata;
  contacts: Record<"cellPhone" | "phone" | "email", { populated: number; cleared: number; unknown: number }>;
  assignments: { known: number; unassigned: number; unknown: number; conflict: number };
  retired: number; knownTeamDrivers: number;
  mapping: { matched: number; unmatched: number; conflict: number };
}
export interface SbAssetDrivers {
  enabled: boolean; observedAt: string | null; importedAt: string | null; stale: boolean;
  drivers: { name: string; assignmentState: string; driverId?: string | null;
    contacts?: SbDriverContact["contacts"]; contactStates?: Record<"cellPhone" | "phone" | "email", "known" | "unknown">;
    team?: SbDriverContact["team"]; teamNames?: string[]; assignmentObservedAt?: string | null }[];
}
